import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  adminLogin,
  adminListBookings,
  adminDeleteBooking,
  adminSaveContent,
  adminSaveBlockedDates,
  getBlockedDates,
} from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { DEFAULT_CONTENT } from "@/config/content";

const TOKEN_KEY = "trevor_admin_token";
const TABS = [
  { id: "bookings", label: "Bookings" },
  { id: "content", label: "Content" },
  { id: "dates", label: "Blocked Dates" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bookings");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token: t } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      toast.success("Access granted.");
    } catch {
      toast.error("Wrong password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#080808] flex flex-col">
        <div className="p-6 md:p-10 border-b border-[#2C2C2C]">
          <Link to="/" data-testid="admin-back-home" className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-[#C67022] transition-colors">
            ← Trevor
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6">
          <motion.form
            onSubmit={submit}
            data-testid="admin-login-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md border border-[#2C2C2C] bg-[#0d0d0d] p-10"
          >
            <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C67022] mb-4">Restricted</div>
            <h1 className="font-display text-5xl tracking-tighter leading-none mb-8">The back room.</h1>
            <label className="block">
              <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">Password</span>
              <Input
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 h-11 text-lg font-display focus-visible:ring-0 focus-visible:border-[#C67022]"
                placeholder="•••••••••"
                autoFocus
              />
            </label>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="mt-8 w-full border border-[#C67022] bg-[#C67022] text-[#080808] hover:bg-[#E68A35] font-mono text-[11px] tracking-[0.3em] uppercase py-4 transition-colors disabled:opacity-50"
            >
              {loading ? "Unlocking…" : "Enter"}
            </button>
          </motion.form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="border-b border-[#2C2C2C]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-baseline gap-6">
            <Link to="/" className="font-display text-2xl tracking-tighter">Trevor</Link>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">/ Back Room</span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-[#C67022] transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="border-b border-[#2C2C2C]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex gap-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`py-5 font-mono text-[11px] tracking-[0.3em] uppercase transition-colors border-b-2 ${
                tab === t.id
                  ? "text-[#C67022] border-[#C67022]"
                  : "text-white/50 border-transparent hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-14">
        {tab === "bookings" && <BookingsTab token={token} />}
        {tab === "content" && <ContentTab token={token} />}
        {tab === "dates" && <BlockedDatesTab token={token} />}
      </div>
    </div>
  );
}

/* ---------- Bookings tab ---------- */
function BookingsTab({ token }) {
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    try {
      const list = await adminListBookings(token);
      setBookings(list);
    } catch {
      toast.error("Session expired. Sign in again.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    try {
      await adminDeleteBooking(id, token);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <>
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C67022] mb-2">Ledger</div>
          <h1 className="font-display text-6xl md:text-7xl tracking-tighter leading-none">
            {bookings.length}{" "}
            <em className="italic text-white/40">
              {bookings.length === 1 ? "reservation" : "reservations"}
            </em>
          </h1>
        </div>
      </div>

      {bookings.length === 0 ? (
        <div data-testid="admin-empty" className="border border-[#2C2C2C] p-16 text-center text-white/40 font-mono text-xs tracking-[0.3em] uppercase">
          The book is empty. Quiet night.
        </div>
      ) : (
        <div data-testid="admin-bookings-table" className="border border-[#2C2C2C]">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2C2C2C] font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
            <div className="col-span-2">Date · Time</div>
            <div className="col-span-2">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Where</div>
            <div className="col-span-1 text-right">·</div>
          </div>
          {bookings.map((b) => (
            <div
              key={b.id}
              data-testid={`booking-row-${b.id}`}
              className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#2C2C2C] last:border-b-0 items-start hover:bg-[#0d0d0d] transition-colors"
            >
              <div className="col-span-2">
                <div className="font-display text-lg leading-tight">{b.date}</div>
                <div className="font-mono text-xs text-[#C67022] tracking-[0.2em] mt-1">{b.time_slot}</div>
              </div>
              <div className="col-span-2 font-display text-lg">{b.name}</div>
              <div className="col-span-3 text-white/70 text-sm break-all">{b.email}</div>
              <div className="col-span-2 text-white/60 text-sm">{b.phone || "—"}</div>
              <div className="col-span-2 text-white/60 text-sm">{b.location_preference || "—"}</div>
              <div className="col-span-1 text-right">
                <button
                  data-testid={`admin-delete-${b.id}`}
                  onClick={() => remove(b.id)}
                  className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-red-400 transition-colors"
                >
                  Del
                </button>
              </div>
              {b.message && (
                <div className="col-span-12 border-t border-[#2C2C2C] mt-3 pt-3 text-white/60 text-sm italic">
                  &ldquo;{b.message}&rdquo;
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ---------- Content tab ---------- */
function ContentTab({ token }) {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content)));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveContent(draft, token);
      toast.success("Saved. The site is updated.");
      await refresh();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset all copy and images back to the original defaults?")) return;
    setDraft(JSON.parse(JSON.stringify(DEFAULT_CONTENT)));
    toast.info("Defaults loaded — hit Save to apply.");
  };

  const updateHero = (patch) => setDraft((d) => ({ ...d, hero: { ...d.hero, ...patch } }));
  const updateSite = (patch) => setDraft((d) => ({ ...d, site: { ...d.site, ...patch } }));
  const updateChapter = (i, patch) =>
    setDraft((d) => ({
      ...d,
      chapters: d.chapters.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }));

  return (
    <div className="space-y-14">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C67022] mb-2">Editor</div>
          <h1 className="font-display text-6xl md:text-7xl tracking-tighter leading-none">
            Rewrite the <em className="italic text-white/40">room.</em>
          </h1>
          <p className="mt-4 text-white/50 max-w-lg">
            Change photos, videos, or copy — click Save and the site updates instantly.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="content-reset"
            onClick={resetDefaults}
            className="border border-[#2C2C2C] text-white/60 hover:border-white/40 hover:text-white font-mono text-[11px] tracking-[0.3em] uppercase px-5 py-3 transition-colors"
          >
            Reset
          </button>
          <button
            data-testid="content-save"
            onClick={save}
            disabled={saving}
            className="border border-[#C67022] bg-[#C67022] text-[#080808] hover:bg-[#E68A35] font-mono text-[11px] tracking-[0.3em] uppercase px-6 py-3 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Site meta */}
      <Section title="Site">
        <Grid>
          <Field label="Name">
            <TextInput
              testid="field-site-name"
              value={draft.site.name}
              onChange={(v) => updateSite({ name: v })}
            />
          </Field>
          <Field label="Tagline">
            <TextInput
              testid="field-site-tagline"
              value={draft.site.tagline}
              onChange={(v) => updateSite({ tagline: v })}
            />
          </Field>
          <Field label="Location">
            <TextInput
              testid="field-site-location"
              value={draft.site.location}
              onChange={(v) => updateSite({ location: v })}
            />
          </Field>
          <Field label="Year">
            <TextInput
              testid="field-site-years"
              value={draft.site.years}
              onChange={(v) => updateSite({ years: v })}
            />
          </Field>
        </Grid>
      </Section>

      {/* Hero */}
      <Section title="Hero">
        <Grid>
          <Field label="Image URL">
            <TextInput
              testid="field-hero-image"
              value={draft.hero.image}
              onChange={(v) => updateHero({ image: v })}
              placeholder="https://…"
            />
          </Field>
          <Field label="Video URL (optional — overrides image)">
            <TextInput
              testid="field-hero-video"
              value={draft.hero.video || ""}
              onChange={(v) => updateHero({ video: v })}
              placeholder="https://…mp4"
            />
          </Field>
          <Field label="Currently label">
            <TextInput
              value={draft.hero.currentlyLabel || ""}
              onChange={(v) => updateHero({ currentlyLabel: v })}
            />
          </Field>
          <Field label="Currently body">
            <TextInput
              value={draft.hero.currentlyBody || ""}
              onChange={(v) => updateHero({ currentlyBody: v })}
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Sub-headline">
              <TextArea
                value={draft.hero.subhead || ""}
                onChange={(v) => updateHero({ subhead: v })}
                rows={3}
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Headline lines (one per line — every other line will italicize)">
              <TextArea
                testid="field-hero-lines"
                value={(draft.hero.lines || []).join("\n")}
                onChange={(v) => updateHero({ lines: v.split("\n") })}
                rows={5}
              />
            </Field>
          </div>
        </Grid>
        <MediaPreview src={draft.hero.video || draft.hero.image} isVideo={Boolean(draft.hero.video)} />
      </Section>

      {/* Chapters */}
      {draft.chapters.map((c, i) => (
        <Section key={c.id} title={`${c.number} · ${c.title}`}>
          <Grid>
            <Field label="Number">
              <TextInput value={c.number} onChange={(v) => updateChapter(i, { number: v })} />
            </Field>
            <Field label="Title">
              <TextInput value={c.title} onChange={(v) => updateChapter(i, { title: v })} />
            </Field>
            <Field label="Kicker">
              <TextInput value={c.kicker} onChange={(v) => updateChapter(i, { kicker: v })} />
            </Field>
            <Field label="Tags (comma separated)">
              <TextInput
                value={(c.tags || []).join(", ")}
                onChange={(v) =>
                  updateChapter(i, { tags: v.split(",").map((t) => t.trim()).filter(Boolean) })
                }
              />
            </Field>
            <Field label="Image URL">
              <TextInput
                testid={`field-chapter-${c.id}-image`}
                value={c.image || ""}
                onChange={(v) => updateChapter(i, { image: v })}
              />
            </Field>
            <Field label="Video URL (optional — overrides image)">
              <TextInput
                value={c.video || ""}
                onChange={(v) => updateChapter(i, { video: v })}
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Copy (one paragraph per line)">
                <TextArea
                  value={(c.copy || []).join("\n\n")}
                  onChange={(v) => updateChapter(i, { copy: v.split(/\n\n+/).map((s) => s.trim()).filter(Boolean) })}
                  rows={5}
                />
              </Field>
            </div>
          </Grid>
          <MediaPreview src={c.video || c.image} isVideo={Boolean(c.video)} />
        </Section>
      ))}

      {/* Time slots */}
      <Section title="Available time slots">
        <Field label="Slots (comma separated, HH:MM)">
          <TextInput
            value={(draft.timeSlots || []).join(", ")}
            onChange={(v) =>
              setDraft((d) => ({ ...d, timeSlots: v.split(",").map((t) => t.trim()).filter(Boolean) }))
            }
          />
        </Field>
      </Section>

      {/* Sticky footer save */}
      <div className="sticky bottom-6 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="border border-[#C67022] bg-[#C67022] text-[#080808] hover:bg-[#E68A35] font-mono text-[11px] tracking-[0.3em] uppercase px-8 py-4 transition-colors disabled:opacity-50 shadow-2xl"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Blocked dates tab ---------- */
function BlockedDatesTab({ token }) {
  const [dates, setDates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBlockedDates()
      .then((d) => {
        setDates(d);
        setSelected(d.map((k) => new Date(k + "T00:00:00")));
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const keys = selected.map((d) => format(d, "yyyy-MM-dd"));
      const res = await adminSaveBlockedDates(keys, token);
      setDates(res.dates);
      toast.success(`${res.dates.length} night${res.dates.length === 1 ? "" : "s"} blocked.`);
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-14">
      <div>
        <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C67022] mb-2">Off-limits</div>
        <h1 className="font-display text-6xl md:text-7xl tracking-tighter leading-none">
          Block <em className="italic text-white/40">the night.</em>
        </h1>
        <p className="mt-6 text-white/60 max-w-md font-light leading-relaxed">
          Tap any date to hold it. Guests can&apos;t book blocked nights. Tap again to release.
        </p>
        <div className="mt-10 border border-[#2C2C2C] p-6 bg-[#0d0d0d] w-fit">
          <Calendar
            data-testid="blocked-dates-calendar"
            mode="multiple"
            selected={selected}
            onSelect={(d) => setSelected(d || [])}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            className="text-white"
          />
        </div>
        <button
          data-testid="blocked-dates-save"
          onClick={save}
          disabled={saving}
          className="mt-8 border border-[#C67022] bg-[#C67022] text-[#080808] hover:bg-[#E68A35] font-mono text-[11px] tracking-[0.3em] uppercase px-6 py-3 transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save blocked dates"}
        </button>
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">
          Currently held ({dates.length})
        </div>
        {dates.length === 0 ? (
          <div className="border border-[#2C2C2C] p-10 text-center text-white/40 font-mono text-xs tracking-[0.3em] uppercase">
            No nights blocked.
          </div>
        ) : (
          <div className="border border-[#2C2C2C]">
            {dates.map((d) => (
              <div
                key={d}
                data-testid={`blocked-row-${d}`}
                className="px-5 py-4 border-b border-[#2C2C2C] last:border-b-0 flex items-center justify-between"
              >
                <div className="font-display text-lg">{d}</div>
                <button
                  onClick={() => setSelected((s) => s.filter((x) => format(x, "yyyy-MM-dd") !== d))}
                  className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-red-400 transition-colors"
                >
                  Release
                </button>
              </div>
            ))}
            <div className="px-5 py-3 border-t border-[#2C2C2C] font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
              Remember to save changes.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- primitives ---------- */
function Section({ title, children }) {
  return (
    <section className="border border-[#2C2C2C] bg-[#0d0d0d]">
      <div className="px-6 py-4 border-b border-[#2C2C2C] font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">
        {title}
      </div>
      <div className="p-6 md:p-8 space-y-8">{children}</div>
    </section>
  );
}
function Grid({ children }) {
  return <div className="grid md:grid-cols-2 gap-6">{children}</div>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">{label}</span>
      {children}
    </label>
  );
}
function TextInput({ value, onChange, placeholder, testid }) {
  return (
    <Input
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C67022] h-11 text-base font-display"
    />
  );
}
function TextArea({ value, onChange, rows = 3, testid }) {
  return (
    <Textarea
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="bg-transparent border border-[#2C2C2C] rounded-none px-3 py-2 focus-visible:ring-0 focus-visible:border-[#C67022] text-base font-display resize-none"
    />
  );
}
function MediaPreview({ src, isVideo }) {
  if (!src) return null;
  return (
    <div className="mt-6">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">Preview</div>
      <div className="w-full max-w-md aspect-[16/9] bg-[#080808] border border-[#2C2C2C] overflow-hidden">
        {isVideo ? (
          <video src={src} controls muted className="w-full h-full object-cover" />
        ) : (
          <img src={src} alt="preview" className="w-full h-full object-cover" />
        )}
      </div>
    </div>
  );
}
